import type { RootState } from 'src/redux/store';
import type { IAddressItem } from 'src/types/common';
import type { ICheckoutItem, ICheckoutState } from 'src/types/checkout';

import { union, isEqual } from 'es-toolkit';
import { getStorage } from 'minimal-shared/utils';
import { useLocalStorage } from 'minimal-shared/hooks';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';

import { toast } from 'src/components/snackbar';

import { CampaignContext } from './campaign-context';
import {
  useAddCampaignMutation,
  useUpdateCampaignMutation,
  useTriggerCampaignMutation,
} from '../slice/campaignApi';

// ----------------------------------------------------------------------

const CHECKOUT_STORAGE_KEY = 'app-checkout';

const initialState: ICheckoutState = {
  campaignform: {
    name: '',
    channel: '',
  },
  selectedTemplate: {},
  selectedContacts: [],
  campaignDate: null,
  items: [],
  subtotal: 0,
  total: 0,
  discount: 0,
  shipping: 0,
  billing: null,
  totalItems: 0,
  campaignType: 'now',
  templateVariables: {
    header: [],
    body: [],
    buttons: [],
  },
  isEdit: false,
  campaignId: '',
  selectAllFilter: {
    active: false,
    filterType: 'all',
    ids: [],
  },
};

// ----------------------------------------------------------------------

type CheckoutProviderProps = {
  children: React.ReactNode;
};

export function CampaignProvider({ children }: CheckoutProviderProps) {
  const { t } = useTranslate('translation');

const CHECKOUT_STEPS = useMemo(
  () => [
    t('CREATE_CAMPAIGN'),
    t('SELECT_TAMPLATE'),
    t('SELECT_AUDIENCE'),
    t('PREVIEW'),
    // t('FINISH'),
  ],
  [t]
);

const CAMPAIGN_EDIT = useMemo(
  () => [
    t('EDIT_CAMPAIGN'),
    t('EDIT_TEMPLATE'),
    t('EDIT_AUDIENCE'),
    t('PREVIEW'),
  ],
  [t]
);


  const [createCampaign, { isLoading: isCreating }] = useAddCampaignMutation();
  const [triggerCampaign] = useTriggerCampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaignMutation();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeStep = pathname.includes('') ? Number(searchParams.get('step')) : null;

  const isUserInStep = pathname.includes('step');

  const [loading, setLoading] = useState(true);

  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);

  const { state, setState, setField, resetState } = useLocalStorage<ICheckoutState>(
    CHECKOUT_STORAGE_KEY,
    initialState,
    { initializeWithValue: false }
  );

  const canReset = !isEqual(state, initialState);
  const completed = activeStep === CHECKOUT_STEPS.length;

  const updateTotals = useCallback(() => {
    const totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
    const subtotal = state.items.reduce((total, item) => total + item.quantity * item.price, 0);

    setField('subtotal', subtotal);
    setField('totalItems', totalItems);
    setField('total', subtotal - state.discount + state.shipping);
  }, [setField, state.discount, state.items, state.shipping]);

  useEffect(() => {
    const initializeCheckout = async () => {
      try {
        setLoading(true);
        const restoredValue = getStorage(CHECKOUT_STORAGE_KEY);
        if (restoredValue) {
          updateTotals();
        }
      } finally {
        setLoading(false);
      }
    };

    initializeCheckout();
  }, [updateTotals]);

  const handleStepHeader = useCallback(() => {
    if (activeStep == null) return '';
    return state.isEdit ? CAMPAIGN_EDIT[activeStep] : CHECKOUT_STEPS[activeStep];
  }, [CAMPAIGN_EDIT, CHECKOUT_STEPS, activeStep, state.isEdit]);

  const handleResetCampaignState = useCallback(() => {
    setField('campaignform', { name: '', channel: '' });
    setField('selectedTemplate', {});
    setField('selectedContacts', []);
    setField('campaignDate', null);
    setField('items', []);
    setField('subtotal', 0);
    setField('total', 0);
    setField('discount', 0);
    setField('shipping', 0);
    setField('billing', null);
    setField('totalItems', 0);
    setField('campaignType', 'now');
    setField('templateVariables', {
      header: [],
      body: [],
      buttons: [],
    });
    setField('selectAllFilter', {
      active: false,
      filterType: 'all',
      ids: [],
    });

    setField('isEdit', false);
    setField('campaignId', false);
  }, [setField]);

  const createCampaignFunc = useCallback(
    async (formData: any) => {
      try {
        let response;

        if (state.isEdit) {
          response = await updateCampaign({ ...formData, id: state.campaignId }).unwrap();
        } else {
          response = await createCampaign(formData).unwrap();
        }

        const campaignId = response?.data?.campaign?.id;

        if (campaignId && state.campaignType === 'now') {
          await triggerCampaign(campaignId).unwrap();
        }

        toast.success(
          t(state.isEdit ? 'CAMPAIGN_UPDATED_SUCCESSFULLY' : 'CAMPAIGN_CREATED_SUCCESSFULLY')
        );
        router.replace(paths.campaign.root);
        return true;
      } catch (err: any) {
        
        toast.error(err?.data?.message || 'Something went wrong');
        return false;
      }
    },
    [
      createCampaign,
      updateCampaign,
      triggerCampaign,
      t,
      router,
      paths.campaign.root,
      state.isEdit,
      state.campaignType,
      state.campaignId,
    ]
  );

  const onChangeStep = useCallback(
    (type: 'back' | 'next' | 'go', step?: number) => {
      const stepNumbers = {
        back: (activeStep ?? 0) - 1,
        next: (activeStep ?? 0) + 1,
        go: step ?? 0,
      };

      const targetStep = stepNumbers[type];
      if (targetStep === -1) handleResetCampaignState();
      // if (targetStep === 4) {
      //   const updateFormdata = {
      //     title: state.campaignform.name || '',
      //     templateVariables: state.templateVariables,
      //     message: '',
      //     templateId: state.selectedTemplate.id,
      //     channelId: state.campaignform.channel,
      //     scheduledAt: state.campaignDate,
      //     audience: state.selectedContacts,
      //     senderId: loginApiData && 'id' in loginApiData ? loginApiData.id : '',
      //   };

      //   const success = createCampaignFunc(updateFormdata);
      //   if (!success) return; // ✅ STOP IF FAILED
      // }

      // if (targetStep === 5) {
      //   handleResetCampaignState();
      //   // router.replace(paths.campaign.root);
      //   return;
      // }
      // if (targetStep === 0) setField('selectedTemplate', {});
      const queryString = new URLSearchParams({ step: `${targetStep}` }).toString();
      const redirectPath =
        targetStep === 0 ? paths.campaign.step : `${paths.campaign.step}?${queryString}`;

      router.push(redirectPath);
    },
    [activeStep, router, setField, state]
  );

  const onAddToCart = useCallback(
    (newItem: ICheckoutItem) => {
      const updatedItems = state.items.map((item) => {
        if (item.id === newItem.id) {
          return {
            ...item,
            colors: union(item.colors, newItem.colors),
            quantity: item.quantity + newItem.quantity,
          };
        }
        return item;
      });

      if (!updatedItems.some((item) => item.id === newItem.id)) {
        updatedItems.push(newItem);
      }

      setField('items', updatedItems);
    },
    [setField, state.items]
  );

  const onDeleteCartItem = useCallback(
    (itemId: string) => {
      const updatedItems = state.items.filter((item) => item.id !== itemId);

      setField('items', updatedItems);
    },
    [setField, state.items]
  );

  const onTamplateSelect = useCallback(
    (value: string) => {
      setField('selectedTemplate', value);
    },
    [setField]
  );

  const onContactSelect = useCallback(
    (value: string) => {
      setField('selectedContacts', value);
    },
    [setField]
  );

  const onDateSelect = useCallback(
    (value: string) => {
      setField('campaignDate', value);
    },
    [setField]
  );

  const onChangeItemQuantity = useCallback(
    (itemId: string, quantity: number) => {
      const updatedItems = state.items.map((item) => {
        if (item.id === itemId) {
          return { ...item, quantity };
        }
        return item;
      });

      setField('items', updatedItems);
    },
    [setField, state.items]
  );

  const onCreateBillingAddress = useCallback(
    (address: IAddressItem) => {
      setField('billing', address);
    },
    [setField]
  );

  const onApplyDiscount = useCallback(
    (discount: number) => {
      setField('discount', discount);
    },
    [setField]
  );

  const onApplyShipping = useCallback(
    (shipping: number) => {
      setField('shipping', shipping);
    },
    [setField]
  );

  const onResetCart = useCallback(() => {
    if (completed) {
      resetState(initialState);
    }
  }, [completed, resetState]);

  // RESET STATE ON PAGE CHANGE
  useEffect(() => {
    if (!isUserInStep) handleResetCampaignState();
  }, [isUserInStep, handleResetCampaignState]);

  const memoizedValue = useMemo(
    () => ({
      state,
      setState,
      setField,
      /********/
      activeStep,
      onChangeStep,
      steps: CHECKOUT_STEPS,
      /********/
      canReset,
      loading,
      completed,
      handleStepHeader,
      /********/
      onAddToCart,
      onResetCart,
      onApplyDiscount,
      onApplyShipping,
      onDeleteCartItem,
      onChangeItemQuantity,
      onCreateBillingAddress,
      onTamplateSelect,
      onContactSelect,
      onDateSelect,
      isUpdating,
      isCreating,
    }),
    [
      state,
      loading,
      canReset,
      setField,
      setState,
      completed,
      handleStepHeader,
      activeStep,
      onResetCart,
      onAddToCart,
      onChangeStep,
      onApplyDiscount,
      onApplyShipping,
      onDeleteCartItem,
      onChangeItemQuantity,
      onCreateBillingAddress,
      onTamplateSelect,
      onContactSelect,
      onDateSelect,
      isUpdating,
      isCreating,
    ]
  );

  return <CampaignContext.Provider value={memoizedValue}>{children}</CampaignContext.Provider>;
}
